import { useEffect, useRef } from 'react';

/**
 * A locked item remains discoverable/clickable, but never exposes the
 * protected payload. Only a server-issued entitlement unlocks it.
 */
export default function PremiumAccessDialog({ contentTitle, onClose, onUpgrade }) {
  const upgradeButton = useRef(null);
  const closeButton = useRef(null);

  useEffect(() => {
    const previousFocus = document.activeElement;
    upgradeButton.current?.focus();
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
      if (event.key !== 'Tab') return;
      const first = upgradeButton.current;
      const last = closeButton.current;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4 sm:p-6">
      <button type="button" aria-label="Fermer la fenêtre Premium"
        onClick={onClose}
        className="absolute inset-0 bg-slate-950/60 w-full cursor-default" />
      <section role="dialog" aria-modal="true" aria-labelledby="premium-gate-title"
        aria-describedby="premium-gate-description"
        className="relative w-full max-w-md p-6 sm:p-8 rounded-2xl bg-white dark:bg-slate-800 shadow-2xl border border-purple-200 dark:border-purple-800">
        <span className="inline-flex items-center px-3 py-1 rounded-full bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-200 font-semibold text-xs">
          🔒 Contenu Premium
        </span>
        <h2 id="premium-gate-title" className="text-xl sm:text-2xl font-bold mt-4">
          Passez à Premium pour accéder au contenu
        </h2>
        <p id="premium-gate-description" className="mt-3 text-sm text-secondary">
          {contentTitle ? <><strong>{contentTitle}</strong> est réservé aux abonnés Premium. </> : ''}
          Choisissez une durée d’abonnement pour accéder aux contenus réservés.
          Aucun paiement réel n’est effectué en mode de simulation.
        </p>
        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <button ref={upgradeButton} type="button" onClick={onUpgrade}
            className="flex-1 rounded-xl px-4 py-3 bg-purple-700 hover:bg-purple-800 text-white font-semibold">
            Voir les formules Premium
          </button>
          <button ref={closeButton} type="button" onClick={onClose}
            className="rounded-xl px-4 py-3 border border-color bg-secondary text-primary font-semibold">
            Continuer en Free
          </button>
        </div>
      </section>
    </div>
  );
}
